import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Select from "react-select";
import { SELECT_STYLES, mergeSelectStyles } from "../../utils/selectStyles";
import { CButton, CCollapse, CFormInput, CFormSelect } from "@coreui/react";
import { Tooltip } from "react-tooltip";
import * as Sentry from "@sentry/react";
import DashboardContent from "../../components/DashboardContent";
import { usePermissions } from "../../contexts/PermissionsContext";
import { PERMISSIONS } from "../../utils/permissions";
import RedirectHandler from "../../components/RedirectHandler";
import useCsr from "../../hooks/useCsr";
import { CsrAPI } from "../../apis/CsrAPI";
import QuestionItemInfo from "../../components/QuestionItemInfo";
import { ReactComponent as BinIcon } from "../../assets/images/icons/bin.svg";
import { ReactComponent as PlusIcon } from "../../assets/images/icons/plus.svg";
import { ReactComponent as CopyIcon } from "../../assets/images/icons/copy-white.svg";
import { ReactComponent as CopyOutlineIcon } from "../../assets/images/icons/copy.svg";
import {
  SCS_COMPARISON_TYPE_OPTIONS,
  SCS_GROUPING_TABLE_OPTIONS,
  SCS_TREATMENT_GROUP_ROLE_OPTIONS,
} from "../../utils/scsDescriptionOfStudiesOptions";
import {
  SCS_OEE_COMPARISON_TYPE_LABELS,
  SCS_OEE_COMPARISON_TYPE_VALUES,
  SCS_OEE_INTRO_TEXT,
  SCS_OEE_OTHER_STUDIES_OPTIONS,
  SCS_OEE_QUESTIONS,
} from "../../utils/scsOverallExtentOfExposureQuestions";
import { useScsTableOptions } from "../../hooks/useScsTableOptions";
import { useScsDescriptionOfStudies } from "../../hooks/useScsDescriptionOfStudies";

const isProduction = process.env.NODE_ENV === "production";

const FORM_ID = "scs-overall-extent-of-exposure-form";
const REMOVE_TOOLTIP_ID = "scs-oee-remove";
const DUPLICATE_TOOLTIP_ID = "scs-oee-duplicate";

// Repeatable grouping blocks always keep at least one entry; the first entry is
// non-removable (mirrors the Description of Studies / CSR block pattern).
const MIN_BLOCK_ENTRIES = 1;

const emptyTreatmentGroup = () => ({ name: "", role: "" });
const emptyComparisonPair = () => ({ treatment: "", comparator: "" });

// UI shape of one grouping block. Like the Description of Studies comparison
// blocks it holds every branch answer, so switching the comparison type (Q4)
// never loses what was already typed; only the answers belonging to the
// selected type are persisted (see buildPayload).
const emptyGrouping = (name = "") => ({
  qGroupingName: name,
  // A block standing for a grouping named on the Description of Studies page
  // takes its comparison setup from that page, so it only asks Q1 / Q2 and is
  // saved as a predefined_groupings entry. Every other block is a custom
  // grouping: it asks Q3–Q6 too and is saved as an analysis.
  isPredefined: false,
  // Q1 answer: null until the user picks Yes / No.
  qExposureOtherStudies: null,
  qExposureTable: "",
  // Q3 — custom groupings only.
  qExposureGroupingName: "",
  qExposureComparisonType: "",
  qExposureNonComparativeGroupNames: [""],
  qExposureTreatmentGroups: [emptyTreatmentGroup()],
  qExposureComparisonPairs: [emptyComparisonPair()],
  qExposureDoseGroups: [""],
});

const seedList = list =>
  Array.isArray(list) && list.length > 0 ? [...list] : [""];

// Treatment group roles are saved lowercase ("treatment" / "comparator") but
// shown with the wording of SCS_TREATMENT_GROUP_ROLE_OPTIONS on screen — same
// convention as the Description of Studies page.
const roleForPayload = role => (role || "").toLowerCase();
const roleForDisplay = role =>
  SCS_TREATMENT_GROUP_ROLE_OPTIONS.find(
    option => option.toLowerCase() === (role || "").toLowerCase()
  ) || "";

// Q1 was saved as a boolean, but tolerate the "Yes"/"No" strings an earlier
// payload may hold.
const asYesNo = value => {
  if (typeof value === "boolean") return value;
  if (value === "Yes") return true;
  if (value === "No") return false;
  return null;
};

// Deep-clone a grouping block so a duplicated block is fully independent from
// its source (mutating one never affects the other). Also the base of the
// restore helpers below, where only the fields of the selected comparison type
// are present. `isPredefined` is deliberately not carried over: a copy of a
// predefined grouping is saved as an analysis, so it is a custom grouping.
const cloneGrouping = (group, name) => ({
  ...emptyGrouping(name ?? group?.qGroupingName ?? ""),
  qExposureOtherStudies: asYesNo(group?.qExposureOtherStudies),
  qExposureTable: group?.qExposureTable || "",
  qExposureGroupingName: group?.qExposureGroupingName || "",
  qExposureComparisonType: group?.qExposureComparisonType || "",
  qExposureNonComparativeGroupNames: seedList(
    group?.qExposureNonComparativeGroupNames
  ),
  qExposureTreatmentGroups: (group?.qExposureTreatmentGroups?.length
    ? group.qExposureTreatmentGroups
    : [emptyTreatmentGroup()]
  ).map(tg => ({ name: tg?.name || "", role: roleForDisplay(tg?.role) })),
  qExposureComparisonPairs: (group?.qExposureComparisonPairs?.length
    ? group.qExposureComparisonPairs
    : [emptyComparisonPair()]
  ).map(pair => ({
    treatment: pair?.treatment || "",
    comparator: pair?.comparator || "",
  })),
  qExposureDoseGroups: seedList(group?.qExposureDoseGroups),
});

// --- Saved -> UI shape ----------------------------------------------------
//
// The answers are saved as two lists (see buildPayload):
//   predefined_groupings  one entry per grouping named on the Description of
//                         Studies page: { qExposureOtherStudies,
//                         qGroupingName, qExposureTable }
//   analyses              one entry per block added here, holding its table and
//                         the comparison groupings reported on it:
//                         { qExposureOtherStudies?, qExposureTable, groupings }
// A saved grouping carries its name (Q3), the comparison type it was answered
// with and only that type's branch answers (see
// SCS_OEE_COMPARISON_TYPE_VALUES):
//   single_arm   qExposureTreatmentGroups: [{ name, role: "" }, …]
//   multi_arm    qExposureTreatmentGroups: [{ name, role }, …]
//                qExposureComparisonPairs: [{ treatment, comparator }, …]
//   dose_finding qExposureDoseGroups: ["TG4 Name", …]
// The single_arm names are saved under the same attribute as the multi_arm
// treatment groups but belong to the Non-Comparative question on screen, so
// they are put back on that one; Q4 goes back to its on-screen wording.
const restoreGrouping = saved => {
  const comparisonType =
    SCS_OEE_COMPARISON_TYPE_LABELS[saved?.qExposureComparisonType] || "";
  const treatmentGroups = Array.isArray(saved?.qExposureTreatmentGroups)
    ? saved.qExposureTreatmentGroups
    : [];
  // Both branches tolerate either spelling of an entry ("TG1" / { name: "TG1" }).
  const asName = tg => (typeof tg === "string" ? tg : tg?.name || "");
  return cloneGrouping({
    ...saved,
    qExposureComparisonType: comparisonType,
    qExposureNonComparativeGroupNames:
      comparisonType === "Non-Comparative"
        ? treatmentGroups.map(asName)
        : undefined,
    qExposureTreatmentGroups:
      comparisonType === "Comparative"
        ? treatmentGroups.map(tg => ({
            name: asName(tg),
            role: typeof tg === "string" ? "" : tg?.role,
          }))
        : undefined,
  });
};

// Q1 is optional in the payload: an entry that reports a table or a comparison
// grouping is reporting something, so it comes back answered "Yes" even when
// the flag itself was left out.
const restoreOtherStudies = (value, hasContent) => {
  const answer = asYesNo(value);
  return answer === null && hasContent ? true : answer;
};

// A predefined grouping is saved with its name, Q1 and the table it reports —
// the comparison setup of those groupings comes from the Description of Studies
// page, so they carry no comparison answers.
const restorePredefinedGrouping = (entry, name) => ({
  ...cloneGrouping(
    {
      qExposureOtherStudies: restoreOtherStudies(
        entry?.qExposureOtherStudies,
        Boolean(entry?.qExposureTable)
      ),
      qExposureTable: entry?.qExposureTable,
    },
    name ?? entry?.qGroupingName ?? ""
  ),
  isPredefined: true,
});

// One block per saved analysis. A block holds a single comparison grouping, so
// an analysis carrying several comes back as one block per grouping, each on
// the analysis' table — nothing saved is dropped.
const restoreAnalysisBlocks = analyses =>
  (Array.isArray(analyses) ? analyses : []).flatMap(analysis => {
    const groupings = Array.isArray(analysis?.groupings)
      ? analysis.groupings
      : [];
    const common = {
      qExposureOtherStudies: restoreOtherStudies(
        analysis?.qExposureOtherStudies,
        Boolean(analysis?.qExposureTable) || groupings.length > 0
      ),
      qExposureTable: analysis?.qExposureTable,
    };
    if (!groupings.length) return [cloneGrouping(common)];
    return groupings.map(grouping =>
      restoreGrouping({ ...common, ...grouping })
    );
  });

// The saved answers arrive either bare or wrapped in the section name.
const savedSectionFrom = payload => {
  const section = payload?.extent_of_exposure || payload;
  return section && typeof section === "object" ? section : null;
};

// One block per grouping named in Q2A of the Description of Studies page,
// pre-filled from its saved predefined_groupings entry (matched by name, so the
// pairing survives groupings being added, removed or reordered on that page).
// A saved entry whose grouping is no longer named there keeps its own block
// rather than being dropped, and the saved analyses follow at the end.
const buildGroupings = (names, saved) => {
  const savedPredefined = Array.isArray(saved?.predefined_groupings)
    ? saved.predefined_groupings
    : [];
  const byName = new Map();
  savedPredefined.forEach(entry => {
    const name = (entry?.qGroupingName || "").trim();
    if (name && !byName.has(name)) byName.set(name, entry);
  });

  const matched = new Set();
  const blocks = names.map(name => {
    const entry = byName.get(name);
    if (!entry) return { ...emptyGrouping(name), isPredefined: true };
    matched.add(name);
    return restorePredefinedGrouping(entry, name);
  });

  blocks.push(
    ...savedPredefined
      .filter(entry => !matched.has((entry?.qGroupingName || "").trim()))
      .map(entry => restorePredefinedGrouping(entry))
  );
  blocks.push(...restoreAnalysisBlocks(saved?.analyses));

  return blocks.length > 0 ? blocks : [emptyGrouping()];
};

const toOptions = values => (values || []).map(v => ({ value: v, label: v }));

// Render a single-select's chosen value as a chip/tag so it matches the styling
// used by the Description of Studies / Subject Disposition dropdowns.
const SINGLE_VALUE_CHIP_STYLES = mergeSelectStyles(SELECT_STYLES, {
  singleValue: provided => ({
    ...provided,
    display: "inline-flex",
    alignItems: "center",
    maxWidth: "calc(100% - 8px)",
    backgroundColor: "#eef1f5",
    borderRadius: "4px",
    padding: "3px 6px",
    margin: "2px",
    fontSize: "13px",
    boxSizing: "border-box",
  }),
});

const SCSOverallExtentOfExposure = () => {
  const { studyId: summaryId } = useParams();
  const { setAjaxLoading, wrongEnvironment } = useCsr();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();

  // scs:wizards:read gates the GET that pre-populates this page; writing it
  // back needs scs:wizards:update on top, so without either the Save button is
  // disabled and Next navigates without submitting.
  const canReadWizards = hasPermission(PERMISSIONS.scsWizardsRead);
  const canSaveWizards =
    canReadWizards && hasPermission(PERMISSIONS.scsWizardsUpdate);

  // One accordion per grouping named in Q2A of the Description of Studies page,
  // read from the `scsData` store branch (fetched by the hook when this page is
  // landed on directly).
  const {
    groupingNames,
    isLoadingData: isLoadingGroupings,
    fetched: groupingsFetched,
  } = useScsDescriptionOfStudies(summaryId);

  // The Q2 table dropdown is populated from the safety-summary TFL content in
  // the scsData store once the uploaded files have been PROCESSED; until then
  // fall back to the bundled placeholder options.
  const { tableNames: scsTableNames } = useScsTableOptions();
  const tableOptions = scsTableNames.length
    ? scsTableNames
    : SCS_GROUPING_TABLE_OPTIONS;

  const [questionText, setQuestionText] = useState({});
  const [groupings, setGroupings] = useState([]);
  // The saved section, held until the grouping names are in so both can be
  // merged in one go (they arrive independently).
  const [savedSection, setSavedSection] = useState(null);
  const [savedLoaded, setSavedLoaded] = useState(false);
  // Which grouping accordion items are open (by array index). The first is
  // expanded when the page loads; a newly added / duplicated block (always the
  // last) becomes the only expanded one.
  const [openGroupIndexes, setOpenGroupIndexes] = useState(() => new Set([0]));

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showRedirect, setShowRedirect] = useState(false);

  // Accordion header buttons, so answering "No" can move focus to the next
  // grouping.
  const accordionButtonRefs = useRef({});
  const pendingFocusRef = useRef(null);
  const lastGroupRef = useRef(null);
  const shouldScrollGroupRef = useRef(false);

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Question wording is served from the shared safety-summary questions
  // endpoint; fall back to the bundled constants when it is unavailable.
  useEffect(() => {
    CsrAPI.getSafetySummaryQuestions()
      .then(data => {
        if (mountedRef.current && data) setQuestionText(data);
      })
      .catch(error => {
        if (isProduction) Sentry.captureException(error);
      });
  }, []);

  // Pre-populate previously saved wizard state.
  useEffect(() => {
    if (!summaryId) return;
    // Without scs:wizards:read the endpoint is never called; the page starts
    // from its empty form instead.
    if (!canReadWizards) return;

    setAjaxLoading(true);
    CsrAPI.getSafetySummaryOverallExtentOfExposure({ summaryId })
      .then(data => {
        if (!mountedRef.current) return;
        setSavedSection(savedSectionFrom(data));
      })
      .catch(error => {
        if (!mountedRef.current) return;
        // A 404 simply means nothing has been saved yet — start with defaults.
        if (error?.response?.status === 404) return;
        if (isProduction) Sentry.captureException(error);
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to load previously saved data.");
        }
      })
      .finally(() => {
        if (!mountedRef.current) return;
        setSavedLoaded(true);
        setAjaxLoading(false);
      });
  }, [summaryId, setAjaxLoading, canReadWizards]);

  // Rebuild the blocks whenever the grouping names change (they load after the
  // first render, and can be edited on the Description of Studies page between
  // visits) or the saved answers land. Only the first grouping is expanded.
  // `groupingNames` is memoized by the hook, so this doesn't fire on every
  // render and can't discard what the user has typed.
  useEffect(() => {
    if (!groupingsFetched || !savedLoaded) return;
    setGroupings(buildGroupings(groupingNames, savedSection));
    setOpenGroupIndexes(new Set([0]));
  }, [groupingNames, groupingsFetched, savedLoaded, savedSection]);

  // Scroll the newly added / duplicated grouping into view. Walk up to the
  // named scroll container so we never scroll the browser viewport (matches the
  // Description of Studies block behaviour).
  useEffect(() => {
    if (!shouldScrollGroupRef.current || !lastGroupRef.current) return;
    const el = lastGroupRef.current;
    let wrapper = el.parentElement;
    while (
      wrapper &&
      !wrapper.classList.contains("dashboard-content__wrapper")
    ) {
      wrapper = wrapper.parentElement;
    }
    if (wrapper) {
      const elTop = el.getBoundingClientRect().top;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      wrapper.scrollBy({ top: elTop - wrapperTop, behavior: "smooth" });
    }
    shouldScrollGroupRef.current = false;
  }, [groupings.length]);

  // Answering "No" hands over to the next grouping, so focus follows it there
  // once the collapse/expand has rendered.
  useEffect(() => {
    const target = pendingFocusRef.current;
    if (target === null || target === undefined) return;
    pendingFocusRef.current = null;
    accordionButtonRefs.current[target]?.focus();
  });

  // --- Grouping blocks ----------------------------------------------------
  // "Add another group" appends an empty block; "Duplicate group above"
  // appends a copy of the last block; the per-block "Duplicate" pill appends a
  // copy of that block. New blocks always go to the end and become the only
  // expanded accordion item (mirrors the Description of Studies page).
  const addGrouping = () =>
    setGroupings(prev => {
      const next = [...prev, emptyGrouping()];
      shouldScrollGroupRef.current = true;
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const duplicateGrouping = index =>
    setGroupings(prev => {
      if (!prev[index]) return prev;
      const next = [...prev, cloneGrouping(prev[index])];
      shouldScrollGroupRef.current = true;
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const duplicateGroupingAbove = () => {
    if (!groupings.length) return;
    duplicateGrouping(groupings.length - 1);
  };
  const removeGrouping = index =>
    setGroupings(prev => {
      if (prev.length <= MIN_BLOCK_ENTRIES) return prev;
      const next = prev.filter((_, i) => i !== index);
      // Leave the last remaining grouping expanded.
      setOpenGroupIndexes(new Set([next.length - 1]));
      return next;
    });
  const toggleGroup = index =>
    setOpenGroupIndexes(prev => {
      const next = new Set(prev);
      next.has(index) ? next.delete(index) : next.add(index);
      return next;
    });

  const updateGrouping = (index, updater) =>
    setGroupings(prev =>
      prev.map((row, i) => (i === index ? updater(row) : row))
    );

  // Q1 — "No" ends this grouping's section: collapse it and hand over to the
  // next grouping (expanded and focused) when there is one.
  const answerOtherStudies = (index, value) => {
    updateGrouping(index, row => ({ ...row, qExposureOtherStudies: value }));
    if (value !== false) return;

    const nextIndex = index + 1;
    const hasNext = nextIndex < groupings.length;
    setOpenGroupIndexes(prev => {
      const next = new Set(prev);
      next.delete(index);
      if (hasNext) next.add(nextIndex);
      return next;
    });
    if (hasNext) pendingFocusRef.current = nextIndex;
  };

  // Repeatable lists always keep at least one entry; the first is non-removable.
  const baseGroupNames = row =>
    (row.qExposureNonComparativeGroupNames || []).length > 0
      ? row.qExposureNonComparativeGroupNames
      : [""];
  const baseTreatmentGroups = row =>
    (row.qExposureTreatmentGroups || []).length > 0
      ? row.qExposureTreatmentGroups
      : [emptyTreatmentGroup()];
  const basePairs = row =>
    (row.qExposureComparisonPairs || []).length > 0
      ? row.qExposureComparisonPairs
      : [emptyComparisonPair()];
  const baseDoseGroups = row =>
    (row.qExposureDoseGroups || []).length > 0 ? row.qExposureDoseGroups : [""];

  // Build the grouping a custom block reports, decided by Q4. It is saved with
  // the name given in Q3 and only the answers belonging to the comparison type
  // picked, under the attributes of the kind of grouping that type is saved as:
  //   single_arm   -> qExposureTreatmentGroups (no role question on screen, so
  //                   the names are saved with an empty role)
  //   multi_arm    -> qExposureTreatmentGroups ({ name, role }) and
  //                   qExposureComparisonPairs
  //   dose_finding -> qExposureDoseGroups
  // With Q4 unanswered only the Q3 name is saved, and with neither answered
  // there is nothing to save at all: the block's table is kept on the analysis
  // itself, so nothing is lost on reload.
  const buildGroupingPayload = row => {
    const groupingName = (row.qExposureGroupingName || "").trim();
    const named = groupingName ? { qExposureGroupingName: groupingName } : {};
    const comparisonType =
      SCS_OEE_COMPARISON_TYPE_VALUES[row.qExposureComparisonType];
    if (comparisonType === "single_arm") {
      return {
        ...named,
        qExposureComparisonType: comparisonType,
        qExposureTreatmentGroups: baseGroupNames(row)
          .map(name => (name || "").trim())
          .filter(Boolean)
          .map(name => ({ name, role: "" })),
      };
    }
    if (comparisonType === "multi_arm") {
      return {
        ...named,
        qExposureComparisonType: comparisonType,
        qExposureTreatmentGroups: baseTreatmentGroups(row)
          .filter(tg => (tg?.name || "").trim())
          .map(tg => ({ name: tg.name, role: roleForPayload(tg.role) })),
        qExposureComparisonPairs: basePairs(row).filter(
          pair => pair?.treatment || pair?.comparator
        ),
      };
    }
    if (comparisonType === "dose_finding") {
      return {
        ...named,
        qExposureComparisonType: comparisonType,
        qExposureDoseGroups: baseDoseGroups(row)
          .map(dose => (dose || "").trim())
          .filter(Boolean),
      };
    }
    return groupingName ? named : null;
  };

  // A block added on this page is saved as an analysis: the table it reports
  // and the comparison groupings on it. Answering "No" to Q1 ends the block, so
  // it reports nothing.
  const buildAnalysisPayload = row => {
    const grouping =
      row.qExposureOtherStudies === true ? buildGroupingPayload(row) : null;
    return {
      // Q1 is optional; it is only written once it has been answered.
      ...(row.qExposureOtherStudies === null ||
      row.qExposureOtherStudies === undefined
        ? {}
        : { qExposureOtherStudies: row.qExposureOtherStudies }),
      qExposureTable:
        row.qExposureOtherStudies === true ? row.qExposureTable || null : null,
      groupings: grouping ? [grouping] : [],
    };
  };

  const buildPayload = () => {
    const predefinedGroupings = [];
    const analyses = [];
    const named = new Set();

    groupings.forEach(row => {
      const name = (row.qGroupingName || "").trim();
      // A predefined block stands for one of the Description of Studies
      // groupings. Its comparison setup comes from that page, so only the name,
      // Q1 and the table it reports are saved. A duplicate of such a block is a
      // custom grouping repeating the name, so it is saved as an analysis
      // instead — that keeps one predefined entry per grouping and still saves
      // what was answered in the copy.
      if (row.isPredefined && name && !named.has(name)) {
        named.add(name);
        predefinedGroupings.push({
          qExposureOtherStudies: row.qExposureOtherStudies,
          qGroupingName: name,
          qExposureTable:
            row.qExposureOtherStudies === true
              ? row.qExposureTable || null
              : null,
        });
        return;
      }
      analyses.push(buildAnalysisPayload(row));
    });

    return { predefined_groupings: predefinedGroupings, analyses };
  };

  // Persists the wizard state via PATCH .../wizards/extent_of_exposure.
  // Shared by the "Save" button (stays on the page) and the "Analysis of
  // Adverse Events" button (navigates on success); returns whether the save
  // succeeded so the caller can decide what to do next.
  const saveWizardState = async () => {
    if (isSubmitting) return false;
    if (!canSaveWizards) return false;

    setIsSubmitting(true);
    try {
      await CsrAPI.saveSafetySummaryOverallExtentOfExposure({
        summaryId,
        state: buildPayload(),
      });
      return true;
    } catch (error) {
      if (isProduction) Sentry.captureException(error);
      if (mountedRef.current) {
        if (error?.response?.status === 403) {
          setShowRedirect(true);
        } else {
          toast.error("Failed to save. Please try again.");
        }
      }
      return false;
    } finally {
      if (mountedRef.current) setIsSubmitting(false);
    }
  };

  const handleSubmit = async event => {
    event.preventDefault();
    if (!canSaveWizards) {
      navigate(`/scs/${summaryId}/analysis-of-adverse-events`);
      return;
    }

    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      navigate(`/scs/${summaryId}/analysis-of-adverse-events`);
    }
  };

  const handleSave = async event => {
    event?.preventDefault();
    const saved = await saveWizardState();
    if (saved && mountedRef.current) {
      toast.success("Data saved successfully!");
    }
  };

  const stringSelectOptions = values => [
    { value: "", label: "Select..." },
    ...values.map(v => ({ value: v, label: v })),
  ];

  const removeButton = (label, onClick) => (
    <CButton
      color={"danger"}
      shape={"pill"}
      aria-label={label}
      data-tooltip-id={REMOVE_TOOLTIP_ID}
      data-tooltip-content={label}
      onClick={onClick}>
      <BinIcon />
    </CButton>
  );

  const addButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <PlusIcon />
    </CButton>
  );

  // Solid-blue pill sitting at the top of each grouping block.
  const duplicateBlockButton = onClick => (
    <CButton
      color={"primary"}
      shape={"pill"}
      className={"scs-dos__duplicate"}
      data-tooltip-id={DUPLICATE_TOOLTIP_ID}
      data-tooltip-content={"Duplicate this grouping"}
      onClick={onClick}>
      <CopyIcon /> Duplicate
    </CButton>
  );

  // Outlined pill pairing with "Add another group" at the bottom of the page.
  const duplicateAboveButton = (label, onClick) => (
    <CButton
      color={"primary"}
      variant={"outline"}
      className={"btn-light scs-dos__add"}
      onClick={onClick}>
      {label} <CopyOutlineIcon />
    </CButton>
  );

  // Endpoint wording wins; fall back to the bundled question text.
  const qText = key => {
    const question = SCS_OEE_QUESTIONS[key];
    const served = [key, ...(question?.textKeys || [])]
      .map(name => questionText[name])
      .find(Boolean);
    return served || question?.text || "";
  };

  // CSR-style numbered question: index badge in the left gutter, label +
  // control in the item column (mirrors QuestionItem).
  const renderQuestion = (key, control, numberOverride) => {
    const question = SCS_OEE_QUESTIONS[key];
    if (!question) return null;
    return (
      <div className={"questions-content__container"}>
        <div className={"questions-content__questionIndexContainer"}>
          <span className={"questions-content__label-index"}>
            {numberOverride ?? question.number}
          </span>
        </div>
        <div className={"questions-content__item"}>
          <div className={"questions-content__label"}>
            <span className={"questions-content__label-text"}>
              {qText(key)}
              {question.required && (
                <span className={"questions-content__required-asterisk"}>
                  {" "}
                  *
                </span>
              )}
            </span>
            {question.tooltip && (
              <QuestionItemInfo
                questionName={key}
                notes={{ [key]: question.tooltip }}
              />
            )}
          </div>
          <div className={"questions-content__input"}>{control}</div>
        </div>
      </div>
    );
  };

  // Q1 — Yes / No toggle. "No" ends this grouping's section.
  const renderOtherStudies = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureOtherStudies",
      <>
        {SCS_OEE_OTHER_STUDIES_OPTIONS.map(option => (
          <React.Fragment key={String(option.value)}>
            <input
              type={"radio"}
              className={"btn-check"}
              name={`exposure-other-studies-${index}`}
              id={`exposure-other-studies-${index}-${option.label}`}
              autoComplete={"off"}
              checked={group.qExposureOtherStudies === option.value}
              onChange={() => answerOtherStudies(index, option.value)}
            />
            <label
              className={"btn btn-light"}
              htmlFor={`exposure-other-studies-${index}-${option.label}`}>
              {option.label}
            </label>
          </React.Fragment>
        ))}
      </>,
      numberOverride
    );

  // Q2 — single-select of the SCS TFL tables.
  const renderTable = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureTable",
      <Select
        className={"scs-dos__control"}
        styles={SINGLE_VALUE_CHIP_STYLES}
        options={toOptions(tableOptions)}
        value={
          group.qExposureTable
            ? { value: group.qExposureTable, label: group.qExposureTable }
            : null
        }
        onChange={selected =>
          updateGrouping(index, row => ({
            ...row,
            qExposureTable: selected ? selected.value : "",
          }))
        }
        placeholder={"Select table"}
        isClearable
      />,
      numberOverride
    );

  // Q3 — custom groupings only: the name of the grouping being compared.
  const renderGroupingName = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureGroupingName",
      <div className={"form-component"}>
        <CFormInput
          value={group.qExposureGroupingName || ""}
          placeholder={"Grouping name"}
          onChange={e =>
            updateGrouping(index, row => ({
              ...row,
              qExposureGroupingName: e.target.value,
            }))
          }
        />
      </div>,
      numberOverride
    );

  // Q4 — comparison type, the same single-select button group as Q7 of the
  // Description of Studies page.
  const renderComparisonType = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureComparisonType",
      SCS_COMPARISON_TYPE_OPTIONS.map(option => (
        <React.Fragment key={option}>
          <input
            type={"radio"}
            className={"btn-check"}
            name={`comparison-type-${index}`}
            id={`comparison-type-${index}-${option}`}
            autoComplete={"off"}
            checked={(group.qExposureComparisonType || "") === option}
            onChange={() =>
              updateGrouping(index, row => ({
                ...row,
                qExposureComparisonType: option,
              }))
            }
          />
          <label
            className={"btn btn-light"}
            htmlFor={`comparison-type-${index}-${option}`}>
            {option}
          </label>
        </React.Fragment>
      )),
      numberOverride
    );

  const renderNonComparativeGroupNames = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureNonComparativeGroupNames",
      <>
        {baseGroupNames(group).map((groupName, gnIndex) => (
          <div
            key={`ncgn-${index}-${gnIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={groupName}
                placeholder={"Please enter treatment group name"}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureNonComparativeGroupNames: baseGroupNames(row).map(
                      (g, i) => (i === gnIndex ? e.target.value : g)
                    ),
                  }))
                }
              />
            </div>
            {baseGroupNames(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove group", () =>
                updateGrouping(index, row => ({
                  ...row,
                  qExposureNonComparativeGroupNames: baseGroupNames(row).filter(
                    (_, i) => i !== gnIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add another", () =>
          updateGrouping(index, row => ({
            ...row,
            qExposureNonComparativeGroupNames: [...baseGroupNames(row), ""],
          }))
        )}
      </>,
      numberOverride
    );

  const renderTreatmentGroups = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureTreatmentGroups",
      <>
        {baseTreatmentGroups(group).map((tg, tgIndex) => (
          <div
            key={`tg-${index}-${tgIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Name</label>
              <CFormInput
                value={tg.name}
                placeholder={"Treatment group name"}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureTreatmentGroups: baseTreatmentGroups(row).map(
                      (t, i) =>
                        i === tgIndex ? { ...t, name: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Role</label>
              <CFormSelect
                value={tg.role}
                options={stringSelectOptions(SCS_TREATMENT_GROUP_ROLE_OPTIONS)}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureTreatmentGroups: baseTreatmentGroups(row).map(
                      (t, i) =>
                        i === tgIndex ? { ...t, role: e.target.value } : t
                    ),
                  }))
                }
              />
            </div>
            {baseTreatmentGroups(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove treatment group", () =>
                updateGrouping(index, row => ({
                  ...row,
                  qExposureTreatmentGroups: baseTreatmentGroups(row).filter(
                    (_, i) => i !== tgIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Treatment Group", () =>
          updateGrouping(index, row => ({
            ...row,
            qExposureTreatmentGroups: [
              ...baseTreatmentGroups(row),
              emptyTreatmentGroup(),
            ],
          }))
        )}
      </>,
      numberOverride
    );

  const renderComparisonPairs = (group, index, numberOverride) => {
    // The Treatment / Comparator dropdowns list the names typed into Q5 for
    // this same grouping (mirrors the Description of Studies Q8 -> Q9 flow).
    const groupNameOptions = stringSelectOptions([
      ...new Set(
        (group.qExposureTreatmentGroups || [])
          .map(tg => (tg?.name || "").trim())
          .filter(Boolean)
      ),
    ]);
    return renderQuestion(
      "qExposureComparisonPairs",
      <>
        {basePairs(group).map((pair, pairIndex) => (
          <div
            key={`pair-${index}-${pairIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <label>Treatment</label>
              <CFormSelect
                value={pair.treatment}
                options={groupNameOptions}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureComparisonPairs: basePairs(row).map((p, i) =>
                      i === pairIndex ? { ...p, treatment: e.target.value } : p
                    ),
                  }))
                }
              />
            </div>
            <div className={"form-component"}>
              <label>Comparator</label>
              <CFormSelect
                value={pair.comparator}
                options={groupNameOptions}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureComparisonPairs: basePairs(row).map((p, i) =>
                      i === pairIndex ? { ...p, comparator: e.target.value } : p
                    ),
                  }))
                }
              />
            </div>
            {pairIndex === 0 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove comparison pair", () =>
                updateGrouping(index, row => ({
                  ...row,
                  qExposureComparisonPairs: basePairs(row).filter(
                    (_, i) => i !== pairIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Comparison Pair", () =>
          updateGrouping(index, row => ({
            ...row,
            qExposureComparisonPairs: [
              ...basePairs(row),
              emptyComparisonPair(),
            ],
          }))
        )}
      </>,
      numberOverride
    );
  };

  const renderDoseGroups = (group, index, numberOverride) =>
    renderQuestion(
      "qExposureDoseGroups",
      <>
        {baseDoseGroups(group).map((dose, doseIndex) => (
          <div
            key={`dose-${index}-${doseIndex}`}
            className={"questions-content__input-group"}>
            <div className={"form-component"}>
              <CFormInput
                value={dose}
                placeholder={"Dose group label"}
                onChange={e =>
                  updateGrouping(index, row => ({
                    ...row,
                    qExposureDoseGroups: baseDoseGroups(row).map((d, i) =>
                      i === doseIndex ? e.target.value : d
                    ),
                  }))
                }
              />
            </div>
            {baseDoseGroups(group).length === 1 ? (
              <span className={"empty-space-layout-helper"}></span>
            ) : (
              removeButton("Remove dose group", () =>
                updateGrouping(index, row => ({
                  ...row,
                  qExposureDoseGroups: baseDoseGroups(row).filter(
                    (_, i) => i !== doseIndex
                  ),
                }))
              )
            )}
          </div>
        ))}
        {addButton("Add Dose Group", () =>
          updateGrouping(index, row => ({
            ...row,
            qExposureDoseGroups: [...baseDoseGroups(row), ""],
          }))
        )}
      </>,
      numberOverride
    );

  // A single grouping block. Answering "No" to Q1 ends the section, so nothing
  // below it is shown. A predefined grouping stops after Q2 — its comparison
  // setup comes from the Description of Studies page — while a custom grouping
  // carries the full set of questions (1–6).
  const renderGroupingBlock = (group, index) => {
    const comparisonType = group.qExposureComparisonType || "";
    const isFirstBlock = index === 0;
    const blockNumber = index + 1;
    // First block keeps the plain page numbers (1..6); every additional block
    // appends "(blockNumber)" to each question index (e.g. 1(2), 3(2), 4(2)) —
    // the same convention the Description of Studies blocks use.
    const q = n => (isFirstBlock ? n : `${n}(${blockNumber})`);
    // Branch questions are numbered sequentially from 5 in render order, based
    // on the single selected comparison type.
    let branchNumber = 5;
    const nonComparativeGroupNamesNumber =
      comparisonType === "Non-Comparative" ? q(branchNumber++) : null;
    const treatmentGroupsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const comparisonPairsNumber =
      comparisonType === "Comparative" ? q(branchNumber++) : null;
    const doseGroupsNumber =
      comparisonType === "Dose-Finding" ? q(branchNumber++) : null;

    return (
      <>
        <div className={"scs-dos__block-actions"}>
          {duplicateBlockButton(() => duplicateGrouping(index))}
          {index > 0 &&
            removeButton("Remove grouping", () => removeGrouping(index))}
        </div>
        {renderOtherStudies(group, index, q(1))}
        {group.qExposureOtherStudies === true && (
          <>
            {renderTable(group, index, q(2))}
            {!group.isPredefined && (
              <>
                {renderGroupingName(group, index, q(3))}
                {renderComparisonType(group, index, q(4))}
                {comparisonType === "Non-Comparative" &&
                  renderNonComparativeGroupNames(
                    group,
                    index,
                    nonComparativeGroupNamesNumber
                  )}
                {comparisonType === "Comparative" && (
                  <>
                    {renderTreatmentGroups(group, index, treatmentGroupsNumber)}
                    {renderComparisonPairs(group, index, comparisonPairsNumber)}
                  </>
                )}
                {comparisonType === "Dose-Finding" &&
                  renderDoseGroups(group, index, doseGroupsNumber)}
              </>
            )}
          </>
        )}
      </>
    );
  };

  const renderGroupings = () => (
    <>
      {/* A single grouping is rendered as a plain block — the accordion (and
          its Expand all / Collapse all controls) only earns its keep from two
          groupings up, as on the Description of Studies and Subject
          Disposition pages. */}
      {groupings.length === 1 ? (
        <div ref={lastGroupRef} className={"analysis-block"}>
          {renderGroupingBlock(groupings[0], 0)}
        </div>
      ) : (
        <>
          <div className={"scs-dos__accordion-controls"}>
            <CButton
              color={"primary"}
              shape={"pill"}
              className={"scs-dos__duplicate"}
              onClick={() =>
                setOpenGroupIndexes(new Set(groupings.map((_, i) => i)))
              }>
              Expand all
            </CButton>
            <CButton
              color={"primary"}
              shape={"pill"}
              className={"scs-dos__duplicate"}
              onClick={() => setOpenGroupIndexes(new Set())}>
              Collapse all
            </CButton>
          </div>
          <div className={"accordion"}>
            {groupings.map((group, index) => {
              const isOpen = openGroupIndexes.has(index);
              const isLast = index === groupings.length - 1;
              return (
                <div
                  key={`grouping-${index}`}
                  className={"accordion-item"}
                  ref={isLast ? lastGroupRef : null}>
                  <div className={"accordion-header"}>
                    <button
                      type={"button"}
                      ref={el => (accordionButtonRefs.current[index] = el)}
                      className={`accordion-button${isOpen ? "" : " collapsed"}`}
                      onClick={() => toggleGroup(index)}>
                      {group.qGroupingName ||
                        group.qExposureGroupingName ||
                        `Grouping ${index + 1}`}
                    </button>
                  </div>
                  <CCollapse visible={isOpen} className={"accordion-collapse"}>
                    <div className={"accordion-body"}>
                      <div className={"analysis-block"}>
                        {renderGroupingBlock(group, index)}
                      </div>
                    </div>
                  </CCollapse>
                </div>
              );
            })}
          </div>
        </>
      )}
      <div className={"scs-dos__block-footer"}>
        {addButton("Add another group", addGrouping)}
        {duplicateAboveButton("Duplicate group above", duplicateGroupingAbove)}
      </div>
    </>
  );

  // Until the grouping names are in we don't know how many accordions to draw,
  // so hold the content back rather than flashing a single empty grouping.
  const isResolvingGroupings =
    (isLoadingGroupings && !groupingsFetched) || !savedLoaded;

  // Reuses the `.scs-dos` styles — the block / accordion / duplicate controls
  // are identical to the Description of Studies wizard.
  const PageContent = () => (
    <div className={"questions-page scs-dos"}>
      <div className={"questions-content"}>
        <div className={"questions-content__items"}>
          <ToastContainer />
          <div className={"questions-content__item"}>
            <div className={"questions-content__label intro-text"}>
              <span className={"questions-content__label-text"}>
                {SCS_OEE_INTRO_TEXT}
              </span>
            </div>
          </div>
          <form id={FORM_ID} onSubmit={handleSubmit}>
            {!isResolvingGroupings && renderGroupings()}
          </form>
          <Tooltip
            id={REMOVE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
          <Tooltip
            id={DUPLICATE_TOOLTIP_ID}
            place={"top"}
            delayShow={100}
            className={"custom-tooltip"}
          />
        </div>
      </div>
    </div>
  );

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Overall Extent of Exposure"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Subject Disposition",
        buttonGoToAction: `/scs/${summaryId}/subject-disposition`,
      }}
      saveButton={true}
      savePermission={[
        PERMISSIONS.scsWizardsRead,
        PERMISSIONS.scsWizardsUpdate,
      ]}
      saveButtonHandler={handleSave}
      saveButtonDisabled={isSubmitting}
      nextButton={{
        buttonTitle: isSubmitting ? "Saving..." : "Analysis of Adverse Events",
        buttonFormAction: FORM_ID,
        buttonGoToAction: `/scs/${summaryId}/analysis-of-adverse-events`,
        disabled: isSubmitting,
      }}
    />
  );
};

export default SCSOverallExtentOfExposure;
